// src/pages/ForgotPassword.jsx
import { useState, useEffect } from 'react';
import API_BASE_URL from '../config';
import { useLocation } from 'react-router-dom';
import { Eye, EyeOff } from "lucide-react";

const ForgotPassword = ({ onBack, autoEmail, autoOtp }) => {
  const [step, setStep] = useState(1); // 1 = email, 2 = otp, 3 = new password, 4 = success
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // SAME PASSWORD REGEX FROM SIGNUP
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

  const location = useLocation();
  const routeState = location.state || {};
  const passedAutoEmail = autoEmail || routeState.autoEmail;
  const passedAutoOtp = autoOtp || routeState.autoOtp;

  useEffect(() => {
    if (passedAutoOtp && passedAutoEmail) {
      setEmail(passedAutoEmail);
      setOtp(passedAutoOtp);
      setStep(2);
      setMessage({ type: 'info', text: 'Please enter the OTP sent to your phone.' });
    } else if (passedAutoEmail) {
      setEmail(passedAutoEmail);
      setStep(1);
    }
  }, [passedAutoOtp, passedAutoEmail]);

  // Validation function
  const validateStep = () => {
    const newErrors = {};

    if (step === 1) {
      if (!email.trim()) {
        newErrors.email = 'Phone number is required';
      }
    } else if (step === 2) {
      if (!otp.trim()) {
        newErrors.otp = 'OTP is required';
      }
    } else if (step === 3) {
      if (!newPassword) {
        newErrors.newPassword = 'New password is required';
      } else if (!passwordRegex.test(newPassword)) {
        newErrors.newPassword = 'Password must be 8+ chars with upper, lower, number, special';
      } else if (newPassword !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 1 - Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true);
    setMessage({});

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(email)
      });

      const data = await res.json();

      if (res.ok) {
        setMessage({ type: 'success', text: data.message });
        setStep(2);
      } else {
        setMessage({ type: 'error', text: data || 'Error sending OTP' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error' });
    }

    setLoading(false);
  };

  // Step 2 - Validate OTP and get reset token
  const handleValidateOtp = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true);
    setMessage({});

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/validate-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: email, Otp: otp })
      });

      const data = await res.json();

      if (res.ok) {
        setResetToken(data.identityToken);
        setMessage({ type: 'success', text: 'OTP validated. Please set your new password.' });
        setStep(3);
      } else {
        setMessage({ type: 'error', text: data || 'Invalid or expired OTP' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error' });
    }

    setLoading(false);
  };

  // Step 3 - Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true);
    setMessage({});

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userName: email,
          ResetToken: resetToken,
          NewPassword: newPassword
        })
      });

      const data = await res.json();

      if (res.ok) {
        setMessage({ type: 'success', text: data.message });
        setStep(4);
        setEmail('');
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
        setResetToken('');
      } else {
        setMessage({ type: 'error', text: (data.errors && data.errors.join(', ')) || data || 'Password reset failed' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error' });
    }

    setLoading(false);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-xl font-semibold text-white mb-2">Reset Password</h2>
        <p className="text-gray-400 text-sm">Enter details to reset your password</p>
      </div>

      {/* Progress Indicator */}
      <div className="flex justify-between items-center mb-6">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step >= 1 ? 'bg-lime-400 text-black' : 'bg-gray-700 text-gray-400 border border-gray-600'
          }`}>
          1
        </div>
        <div className="w-12 h-1 bg-gray-700 rounded-full"></div>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step >= 2 ? 'bg-lime-400 text-black' : 'bg-gray-700 text-gray-400 border border-gray-600'
          }`}>
          2
        </div>
        <div className="w-12 h-1 bg-gray-700 rounded-full"></div>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step >= 3 ? 'bg-lime-400 text-black' : 'bg-gray-700 text-gray-400 border border-gray-600'
          }`}>
          3
        </div>
      </div>

      {/* Step 1: Email/Phone */}
      {step === 1 && (
        <form onSubmit={handleSendOtp} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Phone Number
            </label>
            <input
              type="text"
              placeholder="Enter your registered phone number"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white focus:ring-2 focus:ring-lime-400 focus:border-transparent"
              required
            />
            {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email}</p>}
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-lime-400 hover:bg-lime-300 text-[#181f10] font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send OTP'}
          </button>
        </form>
      )}

      {/* Step 2: OTP */}
      {step === 2 && (
        <form onSubmit={handleValidateOtp} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Enter OTP</label>
            <input
              type="text"
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white focus:ring-2 focus:ring-lime-400 focus:border-transparent"
              maxLength={6}
              required
            />
            {errors.otp && <p className="mt-1 text-xs text-red-400">{errors.otp}</p>}
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-lime-400 hover:bg-lime-300 text-[#181f10] font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
          >
            {loading ? 'Verifying...' : 'Verify OTP'}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="w-full text-sm text-gray-400 hover:text-white hover:underline mt-2"
          >
            Back to Login
          </button>
        </form>
      )}

      {/* Step 3: New Password */}
      {step === 3 && (
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-300 mb-1">New Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white pr-10 focus:ring-2 focus:ring-lime-400 focus:border-transparent"
              required
            />
            {showPassword ? (
              <button
                type="button"
                className="absolute right-3 top-11 text-gray-400 hover:text-gray-200"
                onClick={() => setShowPassword(false)}
              >
                <EyeOff size={20} />
              </button>
            ) : (
              <button
                type="button"
                className="absolute right-3 top-11 text-gray-400 hover:text-gray-200"
                onClick={() => setShowPassword(true)}
              >
                <Eye size={20} />
              </button>
            )}
            {errors.newPassword && <p className="mt-1 text-xs text-red-400">{errors.newPassword}</p>}
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-300 mb-1">Confirm Password</label>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white pr-10 focus:ring-2 focus:ring-lime-400 focus:border-transparent"
              required
            />
            {showConfirmPassword ? (
              <button
                type="button"
                className="absolute right-3 top-11 text-gray-400 hover:text-gray-200"
                onClick={() => setShowConfirmPassword(false)}
              >
                <EyeOff size={20} />
              </button>
            ) : (
              <button
                type="button"
                className="absolute right-3 top-11 text-gray-400 hover:text-gray-200"
                onClick={() => setShowConfirmPassword(true)}
              >
                <Eye size={20} />
              </button>
            )}
            {errors.confirmPassword && <p className="mt-1 text-xs text-red-400">{errors.confirmPassword}</p>}
          </div>

          {/* SAME PASSWORD REQUIREMENTS FROM SIGNUP */}
          <div className="text-xs text-gray-500 space-y-1 p-3 bg-[#222]/50 rounded-lg border border-[#2b2b2b]">
            <p><strong>Password must contain:</strong></p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Minimum 8 characters</li>
              <li>At least one uppercase letter (A–Z)</li>
              <li>At least one lowercase letter (a–z)</li>
              <li>At least one number (0–9)</li>
              <li>At least one special character (!@#$%^&*)</li>
            </ul>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-lime-400 hover:bg-lime-300 text-[#181f10] font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
          >
            {loading ? 'Updating...' : 'Reset Password'}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="w-full text-sm text-gray-400 hover:text-white hover:underline mt-2"
          >
            Back to Login
          </button>
        </form>
      )}

      {/* Step 4: Success */}
      {step === 4 && (
        <div className="space-y-6 text-center">
          <div className="flex flex-col items-center">
            <svg
              className="w-16 h-16 text-green-500 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" strokeWidth="2"></circle>
              <path strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
            </svg>
            <p className="text-xl font-semibold text-green-400 mb-2">
              Password Reset Successful!
            </p>
            <p className="text-gray-300">
              You can now log in with your new password.
            </p>
          </div>
          <button
            type="button"
            onClick={onBack}
            className="w-full px-6 py-3 bg-lime-400 text-[#181f10] font-semibold rounded-lg hover:bg-lime-300 transition shadow-lg"
          >
            Go to Login
          </button>
        </div>
      )}

      {/* Messages */}
      {step !== 4 && message.text && (
        <div className={`p-3 rounded-lg text-sm text-center ${message.type === 'success'
            ? 'bg-green-500/20 border border-green-500 text-green-100'
            : 'bg-red-500/20 border border-red-500 text-red-100'
          }`}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default ForgotPassword;
